import { Box, Chip, Stack, Tooltip, Typography, Button } from '@mui/material'
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import type { ExecutiveState } from 'app/slice/executive.slice'
import { formatDateRange } from 'utils/executive.utils'
import {
  useBranches,
  useDoctors,
  useOperativeTypes,
  useProcedures,
  usePromotions,
} from 'features/executive/shared/hooks/use-reference-data'

interface EmptyStateProps {
  message?: string
  onClearFilters?: () => void
}

interface ExecutiveRootState {
  executive: ExecutiveState
}

// Render a chip per active filter so the user can see *why* this widget has
// no data. A generic "no results" message is hostile — we want to surface the
// exact conditions applied so the user can decide what to relax.
export default function EmptyState({ message, onClearFilters }: EmptyStateProps) {
  const { t } = useTranslation()
  const filters = useSelector((s: ExecutiveRootState) => s.executive.filters)

  // Reference data — shares the module-level cache used by other widgets
  // so this hook tree doesn't pay extra network roundtrips.
  const { data: branches } = useBranches()
  const { data: doctors } = useDoctors()
  const { data: procedures } = useProcedures()
  const { data: opTypes } = useOperativeTypes()
  const { data: promotions } = usePromotions()

  // Resolve a comma-separated id list ("1,3,5") to display names so chips
  // read "สาขา: ทองหล่อ" instead of "สาขา: #1". `collapse=true` truncates
  // to "first, second +N" when 3+ are selected (for filters that can have
  // long lists like doctors); the full list goes into the tooltip.
  const resolveIds = (
    raw: unknown,
    lookup: ReadonlyArray<{ id: number | string; name: string }>,
    collapse: boolean
  ): { display: string; full: string } => {
    if (raw == null || raw === '') return { display: '', full: '' }
    const tokens = String(raw).split(',').map((s) => s.trim()).filter(Boolean)
    const names = tokens.map((tok) => {
      const n = Number(tok)
      const hit = lookup.find((row) => String(row.id) === String(Number.isFinite(n) ? n : tok))
      return hit?.name ?? `#${tok}`
    })
    const full = names.join(', ')
    if (!collapse || names.length <= 2) return { display: full, full }
    return {
      display: `${names.slice(0, 2).join(', ')} +${names.length - 2}`,
      full,
    }
  }

  const dateRangeLabel = filters.dateRange?.start && filters.dateRange?.end
    ? formatDateRange(filters.dateRange.start, filters.dateRange.end)
    : null

  const chips: Array<{ label: string; value: string; tooltip?: string }> = []
  const pushIdChip = (
    enabled: boolean,
    labelKey: string,
    raw: unknown,
    lookup: ReadonlyArray<{ id: number | string; name: string }>,
    collapse = true
  ) => {
    if (!enabled) return
    const { display, full } = resolveIds(raw, lookup, collapse)
    chips.push({
      label: t(labelKey),
      value: display,
      tooltip: display !== full ? full : undefined,
    })
  }

  pushIdChip(filters.branchId != null, 'EXECUTIVE.FILTER.BRANCH', filters.branchId, branches)
  if (filters.patientType) {
    chips.push({
      label: t('EXECUTIVE.FILTER.PATIENT_TYPE'),
      value: t(
        filters.patientType === 'new'
          ? 'EXECUTIVE.FILTER.PATIENT_TYPE_NEW'
          : 'EXECUTIVE.FILTER.PATIENT_TYPE_EXISTING'
      ),
    })
  }
  if (filters.nationality) {
    chips.push({
      label: t('EXECUTIVE.FILTER.NATIONALITY'),
      value: t(
        `EXECUTIVE.FILTER.NATIONALITY_OPTIONS.${filters.nationality}`,
        filters.nationality
      ),
    })
  }
  if (filters.sourceChannel) {
    chips.push({
      label: t('EXECUTIVE.FILTER.SOURCE_CHANNEL'),
      value: filters.sourceChannel,
    })
  }
  pushIdChip(filters.promotionId != null, 'EXECUTIVE.FILTER.PROMOTION', filters.promotionId, promotions)
  pushIdChip(filters.doctorId != null, 'EXECUTIVE.FILTER.DOCTOR', filters.doctorId, doctors)
  pushIdChip(filters.procedureId != null, 'EXECUTIVE.FILTER.PROCEDURE', filters.procedureId, procedures)
  pushIdChip(filters.txCategoryId != null, 'EXECUTIVE.FILTER.TREATMENT_CATEGORY', filters.txCategoryId, opTypes)

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', py: 5, px: 2, color: 'text.secondary', textAlign: 'center',
    }}>
      <InboxOutlinedIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
      <Typography variant="body1" sx={{ fontWeight: 500 }}>
        {message ?? t('EXECUTIVE.EMPTY')}
      </Typography>
      {dateRangeLabel && (
        <Typography variant="caption" sx={{ mt: 0.5 }}>
          {t('EXECUTIVE.EMPTY_DETAIL.PERIOD', { range: dateRangeLabel })}
        </Typography>
      )}
      {chips.length > 0 && (
        <>
          <Typography variant="caption" sx={{ mt: 1 }}>
            {t('EXECUTIVE.EMPTY_DETAIL.ACTIVE_FILTERS')}
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" justifyContent="center"
            sx={{ mt: 0.5, rowGap: 0.5, maxWidth: '100%' }}
          >
            {chips.map((c) => {
              // No maxWidth — the resolveIds collapse already keeps long
              // lists in check ("+N"). Branch stays uncollapsed and
              // wraps naturally if a clinic has many branches selected.
              const chip = (
                <Chip
                  size="small"
                  label={`${c.label}: ${c.value}`}
                  variant="outlined"
                  sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', display: 'block', py: 0.25 } }}
                />
              )
              return c.tooltip ? (
                <Tooltip key={c.label + c.value} title={c.tooltip} arrow>
                  {chip}
                </Tooltip>
              ) : (
                <Box key={c.label + c.value}>{chip}</Box>
              )
            })}
          </Stack>
        </>
      )}
      <Typography variant="caption" sx={{ mt: 1.5, opacity: 0.7 }}>
        {t('EXECUTIVE.EMPTY_DETAIL.HINT')}
      </Typography>
      {onClearFilters && (
        <Button size="small" onClick={onClearFilters} sx={{ mt: 1 }}>
          {t('EXECUTIVE.FILTER.RESET')}
        </Button>
      )}
    </Box>
  )
}
